import styles from "./styles.module.scss";
import { default as SnoozeIcon } from "../../assets/icons/snooze.svg?react";
import { getScopeString } from "../../helpers/utils";
import classnames from "classnames";
import { RiFilter3Line } from "react-icons/ri";
import { Dropdown } from "../../components/form";
import { capitalize } from "lodash";
import { BetterPopover } from "../../components/popover";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { postSnoozeIncident } from "../../helpers/apis";
import { GET_INCIDENTS } from "../../services/filters";
import { isSnoozedFor1Day } from "./helper";
import { SortIcon } from "../../components/tables";
import Icon from "@/components/icon/Icon";
import type { Incident } from "./types";
import { Stack } from "@altimateai/lego";

interface SortProp {
  onIncident: (order: string) => void;
  onPolicy: (order: string) => void;
  incidentSortValue: string;
  policySortValue: string;
}

interface FilterOption {
  label: string;
  value: string;
}

interface FilterProp {
  value: string | ((value: string) => boolean);
  options: FilterOption[];
  onChange: (value: string) => void;
}

interface IncidentListProps {
  incidents: Incident[];
  sort: SortProp;
  levelFilter: FilterProp;
  timeFilter: FilterProp;
  contractFilter: FilterProp;
  onIncidentClick: (id: number) => void;
}

const IncidentList = ({
  incidents,
  sort,
  levelFilter,
  timeFilter,
  contractFilter,
  onIncidentClick,
}: IncidentListProps) => {
  const queryClient = useQueryClient();
  const { mutate } = useMutation({
    mutationFn: postSnoozeIncident,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GET_INCIDENTS] });
    },
  });
  return (
    <div>
      <div className={styles.table}>
        <div className={styles.table_header}>
          <Stack justify="between" align="center" gap="none">
            <span>Incident ID</span>
            <SortIcon onChange={sort.onIncident} sort={sort.incidentSortValue} />
          </Stack>
          <Stack justify="between" align="center" gap="none">
            <span>Policy Name</span>
            <SortIcon onChange={sort.onPolicy} sort={sort.policySortValue} />
          </Stack>
          <Stack justify="between" align="center" gap="none">
            <span>Timestamp</span>
            <Dropdown
              id="timestamp-dropdown"
              value={timeFilter.value}
              options={timeFilter.options}
              onChange={timeFilter.onChange}
            >
              <Icon>
                <RiFilter3Line />
              </Icon>
            </Dropdown>
          </Stack>
          <Stack justify="between" align="center" gap="none">
            <span>Level</span>
            <Dropdown
              isMultiSelect
              id="level-dropdown"
              value={levelFilter.value}
              options={levelFilter.options}
              onChange={levelFilter.onChange}
              showDivider
            >
              <Icon>
                <RiFilter3Line />
              </Icon>
            </Dropdown>
          </Stack>
          <Stack justify="between" align="center" gap="none">
            <span>Scope</span>
          </Stack>
          <Stack justify="between" align="center" gap="none">
            <span>Contract</span>
            <Dropdown
              isMultiSelect
              id="contract-dropdown"
              value={contractFilter.value}
              options={contractFilter.options}
              onChange={contractFilter.onChange}
              label="Contract"
            >
              <Icon>
                <RiFilter3Line />
              </Icon>
            </Dropdown>
          </Stack>
          <Stack justify="between" align="center" gap="none" className={styles.action}>
            <span>Action</span>
          </Stack>
        </div>
        <div className={styles.table_body}>
          {incidents.map(item => {
            const snoozeId = "snooze-" + item.id;
            return (
              <div
                key={item.id}
                className={classnames(styles.table_row, styles.cursor_pointer)}
                role="button"
                tabIndex={0}
                onClick={e => {
                  e.preventDefault();
                  onIncidentClick(item.id);
                }}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onIncidentClick(item.id);
                  }
                }}
              >
                <div className="text-primary">{item.id}</div>
                <div className={"al-break-words"}>{item.policy_name}</div>
                <div>{item.timestamp}</div>
                <div
                  className={classnames("fw-semibold", {
                    [styles.orange]: item.level === "warning",
                    [styles.red]: item.level === "error",
                  })}
                >
                  {capitalize(item.level)}
                </div>
                <div className={classnames(styles.blue, styles.scope, "al-break-words")}>
                  {item.scope.map(getScopeString).join(", ")}
                </div>
                <div className="fw-bold">{item.contract_name}</div>
                <BetterPopover
                  id={`popover-${snoozeId}`}
                  trigger={
                    <div
                      className={classnames(
                        styles.action,
                        item.status === "active" ? styles.green : styles.grey
                      )}
                      role="button"
                      tabIndex={0}
                      onClick={e => e.stopPropagation()}
                      onKeyDown={e => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.stopPropagation();
                        }
                      }}
                    >
                      <SnoozeIcon />
                    </div>
                  }
                  render={({ close }) => (
                    <ul className={styles.list} role="menu">
                      {[
                        {
                          value: "24h",
                          label: "24 hours",
                          predicate:
                            item.status === "snoozed" && isSnoozedFor1Day(item.snoozed_till),
                        },
                        {
                          value: "1w",
                          label: "1 week",
                          predicate:
                            item.status === "snoozed" && !isSnoozedFor1Day(item.snoozed_till),
                        },
                      ].map(i => (
                        <li
                          key={i.value}
                          role="menuitem"
                          className={
                            i.predicate
                              ? styles.active
                              : ["active", "snoozed"].includes(item.status)
                                ? styles.enabled
                                : styles.disabled
                          }
                          onClick={e => {
                            e.stopPropagation();
                            mutate({ id: String(item.id), snoozed_till: i.value });
                            close();
                          }}
                          onKeyDown={e => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.stopPropagation();
                              mutate({ id: String(item.id), snoozed_till: i.value });
                              close();
                            }
                          }}
                        >
                          {i.label}
                        </li>
                      ))}
                    </ul>
                  )}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export { IncidentList };
